<!--
  @component ItemDetailsView
  @description Read-only view of item details with dynamic field rendering per item type
-->
<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import { t } from '@/i18n';
  import { extractOriginalDate, cleanExtraForDisplay } from '@/utils';
  import { getFieldsForItemType, ITEM_TYPES } from '@/data';
  import type { Item, ItemField } from '@/types';

  export let item: Item;

  const dispatch = createEventDispatcher<{
    edit: void;
  }>();

  // Fields that should use special rendering
  const textareaFields: ItemField[] = ['abstractNote', 'extra', 'history', 'references'];
  const linkFields: ItemField[] = ['url', 'DOI'];
  const excludedFields: ItemField[] = ['title', 'creators', 'extra'];

  // Get fields for the current item type
  $: currentFields = getFieldsForItemType(item.data.itemType);

  // Format creators for display
  function formatCreatorDisplay(creator: {
    firstName?: string;
    lastName?: string;
    name?: string;
  }): string {
    if (creator.name) return creator.name;
    const parts = [creator.firstName, creator.lastName].filter(Boolean);
    return parts.join(' ') || '-';
  }

  // Get creator type label
  function getCreatorTypeLabel(creatorType: string): string {
    return $t(`item.creatorTypes.${creatorType}`) || creatorType;
  }

  // Get item type label
  function getItemTypeLabel(itemType: string): string {
    return $t(`item.types.${itemType}`) || itemType;
  }

  // Get field label
  function getFieldLabel(field: string): string {
    return $t(`item.fields.${field}`) || field;
  }

  // Get field value for display
  function getFieldValue(field: ItemField): string {
    const value = item.data[field as keyof typeof item.data];
    if (value === undefined || value === null || value === '') return '';
    return String(value);
  }

  // Check if field should be shown (has value and not excluded)
  function shouldShowField(field: ItemField): boolean {
    if (excludedFields.includes(field)) return false;
    return getFieldValue(field) !== '';
  }

  // Check if field is a textarea field
  function isTextareaField(field: ItemField): boolean {
    return textareaFields.includes(field);
  }

  // Check if field is a link field
  function isLinkField(field: ItemField): boolean {
    return linkFields.includes(field);
  }

  // Get link URL for a field
  function getLinkUrl(field: ItemField, value: string): string {
    if (field === 'DOI') return `https://doi.org/${value}`;
    return value;
  }

  function handleEdit() {
    dispatch('edit');
  }
</script>

<!-- Item Type -->
<div class="field-group">
  <span class="field-label">{$t('item.itemType')}</span>
  <div class="field-value type-badge">
    {ITEM_TYPES[item.data.itemType]?.icon || '📄'}
    {getItemTypeLabel(item.data.itemType)}
  </div>
</div>

<!-- Title -->
<div class="field-group">
  <span class="field-label">{getFieldLabel('title')}</span>
  <div class="field-value title">{item.data.title || '-'}</div>
</div>

<!-- Creators with type -->
<div class="field-group">
  <span class="field-label">{getFieldLabel('creators')}</span>
  <div class="field-value">
    {#if item.data.creators && item.data.creators.length > 0}
      {#each item.data.creators as creator}
        <div class="creator-detail">
          <strong>{getCreatorTypeLabel(creator.creatorType)}:</strong>
          {formatCreatorDisplay(creator)}
        </div>
      {/each}
    {:else}
      <span class="empty-value">-</span>
    {/if}
  </div>
</div>

<!-- Original Date (from Extra) -->
{#if extractOriginalDate(item.data.extra)}
  <div class="field-group highlight">
    <span class="field-label">{getFieldLabel('originalDate')}</span>
    <div class="field-value">{extractOriginalDate(item.data.extra)}</div>
  </div>
{/if}

<!-- Dynamic fields based on item type -->
{#each currentFields as field}
  {#if shouldShowField(field)}
    <div class="field-group">
      <span class="field-label">{getFieldLabel(field)}</span>
      <div class="field-value" class:abstract={isTextareaField(field)}>
        {#if isLinkField(field)}
          <a href={getLinkUrl(field, getFieldValue(field))} target="_blank" rel="noopener">
            {getFieldValue(field)}
          </a>
        {:else}
          {getFieldValue(field)}
        {/if}
      </div>
    </div>
  {/if}
{/each}

<!-- Extra (cleaned, without original date) -->
{#if cleanExtraForDisplay(item.data.extra)}
  <div class="field-group">
    <span class="field-label">{getFieldLabel('extra')}</span>
    <div class="field-value extra">{cleanExtraForDisplay(item.data.extra)}</div>
  </div>
{/if}

<!-- Actions -->
<div class="action-buttons">
  <button class="action-button primary" on:click={handleEdit}>
    {$t('common.edit')}
  </button>
</div>

<style>
  .field-group {
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid var(--color-border-light, #444);
  }

  .field-group:last-of-type:not(.action-buttons) {
    border-bottom: none;
  }

  .field-group.highlight {
    background: rgba(252, 196, 25, 0.1);
    margin: 0 -16px 16px;
    padding: 12px 16px;
    border-bottom: 1px solid var(--color-border-light, #444);
  }

  .field-label {
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: var(--color-text-secondary, #bbb);
    margin-bottom: 4px;
  }

  .field-value {
    font-size: 13px;
    line-height: 1.5;
    word-wrap: break-word;
  }

  .field-value.title {
    font-weight: 500;
    font-size: 14px;
  }

  .field-value.type-badge {
    display: inline-block;
    background: var(--color-bg-tertiary, #3c3c3c);
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
  }

  .field-value a {
    color: var(--color-accent, #5c7cfa);
    text-decoration: underline;
    word-break: break-all;
  }

  .field-value a:hover {
    color: var(--color-accent-hover, #4c6ef5);
  }

  .field-value.abstract,
  .field-value.extra {
    white-space: pre-wrap;
    max-height: 150px;
    overflow-y: auto;
    background: var(--color-bg-primary, #2b2b2b);
    padding: 8px;
    border-radius: 4px;
    border: 1px solid var(--color-border-light, #444);
    font-size: 12px;
  }

  .field-value.extra {
    font-family: 'Courier New', monospace;
  }

  .creator-detail {
    margin-bottom: 4px;
  }

  .creator-detail:last-child {
    margin-bottom: 0;
  }

  .creator-detail strong {
    color: var(--color-text-secondary, #bbb);
  }

  .empty-value {
    color: var(--color-text-muted, #888);
    font-style: italic;
  }

  .action-buttons {
    display: flex;
    gap: 8px;
    margin-top: 16px;
    flex-wrap: wrap;
    border-bottom: none !important;
  }

  .action-button {
    flex: 1;
    min-width: 80px;
    padding: 8px 12px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;
    font-weight: 600;
    transition: background-color 0.2s;
  }

  .action-button.primary {
    background: var(--color-accent, #5c7cfa);
    color: white;
  }

  .action-button.primary:hover {
    background: var(--color-accent-hover, #4c6ef5);
  }
</style>
